import {
    ArrowCcwIcon,
    CheckIcon,
    CircleCheckIcon,
    CircleSubtractIcon,
    EquipmentCheckoutIcon,
    EquipmentReturnIcon,
} from "@xola/icons";
import { Button, Spinner } from "@xola/ui-kit";
import clsx from "clsx";
import React, { ButtonHTMLAttributes } from "react";

interface CheckInButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    isDisabled: boolean;
    isLoading: boolean;
    active: boolean;
    action?: "checkIn" | "noShow" | "return" | "equipmentCheckout" | "equipmentReturn";
    isGuestLevelButton?: boolean;
    className?: string;
}

export const CheckInButton = ({
    isDisabled,
    isLoading,
    active = false,
    action = "checkIn",
    isGuestLevelButton,
    className,
    ...rest
}: CheckInButtonProps) => {
    const activeColor = action === "noShow" ? "danger" : "success";
    // Return-style actions (guest & equipment return) show a grey outline until done; all others (check-in,
    // equipment checkout) show the active-color outline. Active always fills with the active color.
    const isGreyWhenInactive = action === "return" || action === "equipmentReturn";

    return (
        <Button
            variant={active ? "standard" : "outline"}
            color={active ? activeColor : isGreyWhenInactive ? "secondary" : "success"}
            size="small"
            className={clsx(
                "mr-1 mt-1 border",
                !active &&
                    (isGreyWhenInactive
                        ? "border-secondary text-gray-darker"
                        : `border-${activeColor} text-${activeColor}`),
                className,
            )}
            data-testid={`${action}-button`}
            disabled={isDisabled || isLoading}
            {...rest}
        >
            {isLoading ? (
                <Spinner size="tiny" />
            ) : (
                <ActionIcon action={action} active={active} isGuestLevelButton={isGuestLevelButton} />
            )}
        </Button>
    );
};

const ActionIcon = ({
    action,
    active,
    isGuestLevelButton,
}: {
    action: NonNullable<CheckInButtonProps["action"]>;
    active: boolean;
    isGuestLevelButton?: boolean;
}) => {
    switch (action) {
        case "equipmentCheckout":
            return <EquipmentCheckoutIcon />;
        case "equipmentReturn":
            return <EquipmentReturnIcon />;
        case "return":
            return <ArrowCcwIcon />;
        case "noShow":
            return <CircleSubtractIcon />;
        default:
            return isGuestLevelButton ? <CheckIcon className={active ? "" : "text-success"} /> : <CircleCheckIcon />;
    }
};
